<!--

# SField

A wrapper component for various types of form inputs.

It provides the label and error message for the input, along with the necessary aria attributes.

-->
<template>
  <div role="none" class="flex-1 min-w-0 flex flex-col items-stretch">
    <s-label v-if="label || $slots.label" :for="id" :class="description ? 'mb-0.5' : 'mb-1.5'">
      <slot name="label">
        {{ label }}
      </slot>
      <span v-if="tooltip || $slots.tooltip" class="sr-only">,</span>
      <s-tooltip v-if="tooltip || $slots.tooltip">
        <template #tooltip>
          <slot name="tooltip">
            {{ tooltip }}
          </slot>
        </template>
        <s-icon
          name="information"
          width="16"
          height="16"
          class="text-blue-500 opacity-70"
          role="presentation"
        />
      </s-tooltip>
    </s-label>
    <p
      v-if="description || $slots.description"
      :id="ids.description"
      class="text-base text-gray-700 leading-tight mb-2"
    >
      <slot name="description">
        {{ description }}
      </slot>
    </p>
    <slot v-bind="ariaProps" />
    <s-field-error :id="ids.error" v-if="error">
      {{ error }}
    </s-field-error>
  </div>
</template>
<script setup lang="ts">
import {computed, VNode, useSlots} from 'vue';
import SLabel from './SLabel.vue';
import STooltip from './STooltip.vue';
import SIcon from './SIcon.vue';
import SFieldError from './SFieldError.vue';

const {
  id,
  label,
  description,
  tooltip,
  error,
  required = false,
} = defineProps<{
  id: string;
  label?: string | null;
  description?: string | null;
  tooltip?: string | VNode | null;
  error?: string | null;
  required?: boolean;
}>();

const slots = useSlots();

const ids = computed(() => ({
  description: `${id}-description`,
  error: `${id}-error`,
}));

const ariaProps = computed(() => {
  return {
    id: id,
    'aria-invalid': !!error,
    'aria-errormessage': error ? ids.value.error : undefined,
    'aria-describedby': description || slots.description ? ids.value.description : undefined,
    'aria-required': required,
  };
});
</script>
